import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttendanceStatus } from '@tailoring-crm/shared';
import { Badge, type BadgeProps } from '@tailoring-crm/ui';

/**
 * Where one person's day has got to, in a word.
 *
 * The word is never carried by the colour alone — every badge says what it is, because a green
 * pill and a grey one are the same pill to anybody who cannot tell them apart.
 *
 * `late` and `halfDay` ride alongside as small markers rather than as statuses of their own: a
 * person who came in at half past ten is still WORKING, and folding lateness into the status
 * would leave the column unable to say both things at once. They are only ever shown when the
 * server set them — this component computes neither.
 */
const VARIANT_OF: Readonly<Record<AttendanceStatus, NonNullable<BadgeProps['variant']>>> =
  Object.freeze({
    not_started: 'outline',
    working: 'default',
    completed: 'success',
    on_leave: 'secondary',
  });

export function AttendanceStatusBadge({
  status,
  late = false,
  halfDay = false,
}: {
  status: AttendanceStatus;
  late?: boolean;
  halfDay?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge variant={VARIANT_OF[status]}>{t(`attendance.status.${status}`)}</Badge>
      {late ? <Badge variant="warning">{t('attendance.marker.late')}</Badge> : null}
      {halfDay ? <Badge variant="outline">{t('attendance.marker.halfDay')}</Badge> : null}
    </span>
  );
}

export default AttendanceStatusBadge;
